import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import en from './locales/en.json'
import zhCN from './locales/zh-CN.json'
import zhTW from './locales/zh-TW.json'
import {
  readFunctionalStorage,
  shouldPersistFunctionalPreferences,
  writeFunctionalStorage,
} from '@/utils/privacyPreferences'
import { getNetworkStorageKey } from '@/hooks/useNetworkEnvironment'

// Language detection - check localStorage first, then browser language
const LANGUAGE_STORAGE_KEY = getNetworkStorageKey('admin_portal_language')

const getDefaultLanguage = (): string => {
  // Check localStorage
  const savedLanguage = readFunctionalStorage(LANGUAGE_STORAGE_KEY)
  if (savedLanguage && ['en', 'zh-CN', 'zh-TW'].includes(savedLanguage)) {
    return savedLanguage
  }
  
  // Check browser language
  const browserLang = navigator.language
  if (browserLang.startsWith('zh-CN') || browserLang === 'zh') {
    return 'zh-CN'
  } else if (browserLang.startsWith('zh-TW') || browserLang === 'zh-HK') {
    return 'zh-TW'
  }
  
  // Default to English
  return 'en'
}

// Follow the resolved UI language without changing detection or preference storage.
const syncDocumentLanguage = () => {
  const language = i18n.resolvedLanguage;
  if (typeof document !== 'undefined' && language) {
    document.documentElement.lang = language === 'zh' ? 'zh-CN' : language;
  }
};

i18n.on('languageChanged', syncDocumentLanguage);

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: en
      },
      'zh-CN': {
        translation: zhCN
      },
      'zh-TW': {
        translation: zhTW
      }
    },
    lng: getDefaultLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false, // React already escapes by default
    },
    react: {
      useSuspense: false, // Disable suspense mode for better error handling
    }
  })

// Save language changes to localStorage
i18n.on('languageChanged', (lng) => {
  if (!shouldPersistFunctionalPreferences()) {
    return
  }
  writeFunctionalStorage(LANGUAGE_STORAGE_KEY, lng)
})

// Log missing translation keys to help with localization coverage
i18n.on('missingKey', (lngs, namespace, key) => {
  const languages = Array.isArray(lngs) ? lngs.join(',') : lngs
  console.warn(
    `[i18n] Missing translation: namespace="${namespace}" key="${key}" languages="${languages}"`
  )
})

export default i18n
